/* ===== PRODUCT SIZE FINDER START ===== */

if (!customElements.get('product-size-finder')) {
  customElements.define(
    'product-size-finder',
    class ProductSizeFinder extends HTMLElement {
      constructor() {
        super();

        this.tolerance = 2;

        this.metricOrder = [
          'bust',
          'underbust',
          'waist',
          'hips',
          'shoulder'
        ];

        this.lengthTerms = [
          'length',
          'dlugo',
          'longueur',
          'lange',
          'lunghezza',
          'delka',
          'dlzka',
          'hossz',
          'μηκος',
          'довжин',
          'длин',
          'lungime',
          'duljina',
          'dolzina',
          'comprimento',
          'lengte',
          'laengde',
          'langd',
          'pituus',
          'lengde',
          'uzunluk',
          'طول',
          'дължин'
        ];

        this.metricTerms = {
          underbust: [
            'underbust',
            'under bust',
            'pod bius',
            'sous poitrine',
            'unterbrust',
            'sottoseno',
            'bajo pecho',
            'pod prsy',
            'pod prsiami',
            'mell alatti',
            'κατω απο το στηθος',
            'під груд',
            'под груд',
            'sub bust',
            'ispod grud',
            'onderborst',
            'under bryst',
            'under bysten',
            'gogus alti',
            'تحت الصدر'
          ],

          shoulder: [
            'shoulder',
            'ramion',
            'epaule',
            'schulter',
            'spalla',
            'hombro',
            'rameno',
            'vall',
            'ωμ',
            'плеч',
            'umar',
            'rame',
            'ombro',
            'schouder',
            'skulder',
            'axel',
            'olkapaa',
            'omuz',
            'كتف',
            'рамо'
          ],

          hips: [
            'hip',
            'biod',
            'hanche',
            'hufte',
            'fianchi',
            'cadera',
            'boky',
            'csipo',
            'γοφ',
            'ισχι',
            'стег',
            'бедр',
            'sold',
            'kuk',
            'boki',
            'quadril',
            'heup',
            'hofte',
            'hoft',
            'lantio',
            'kalca',
            'ورك',
            'ханш'
          ],

          waist: [
            'waist',
            'tali',
            'taille',
            'vita',
            'cintura',
            'pas',
            'derek',
            'μεση',
            'талі',
            'тали',
            'talie',
            'struk',
            'middel',
            'talje',
            'midja',
            'vyotaro',
            'midje',
            'bel',
            'خصر'
          ],

          bust: [
            'chest',
            'bust',
            'klatce piersiowej',
            'klatka piersiowa',
            'bius',
            'brust',
            'poitrine',
            'petto',
            'seno',
            'pecho',
            'prsa',
            'hrud',
            'mellkas',
            'mell',
            'στηθ',
            'груд',
            'piept',
            'grudi',
            'prsi',
            'peito',
            'borst',
            'bryst',
            'brost',
            'rinta',
            'gogus',
            'صدر',
            'бюст'
          ]
        };
      }

      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.decodeTextDatasets();

        this.chart = this.parseJson(
          '[data-size-finder-chart]'
        );

        this.variants =
          this.parseJson(
            '[data-size-finder-variants]'
          ) || [];

        this.datasets = this.getDatasets();
        this.metricRows = this.getMetricRows();
        this.sizes = this.getSizes();

        this.sizeOptionIndex =
          this.findSizeOptionIndex();

        this.errorElement = this.querySelector(
          '[data-size-finder-error]'
        );

        this.resultElement = this.querySelector(
          '[data-size-finder-result]'
        );

        this.resultTitle = this.querySelector(
          '[data-size-finder-result-title]'
        );

        this.resultMessage = this.querySelector(
          '[data-size-finder-result-message]'
        );

        this.resultSecondary = this.querySelector(
          '[data-size-finder-result-secondary]'
        );

        this.selectButton = this.querySelector(
          '[data-size-finder-select]'
        );

        this.selectButtonSize = this.querySelector(
          '[data-size-finder-select-size]'
        );

        if (!this.isEligible()) return;

        this.showRelevantFields();
        this.registerConciergeBridge();

        this.querySelector(
          '[data-size-finder-calculate]'
        )?.addEventListener(
          'click',
          () => this.calculate()
        );

        this.addEventListener(
          'input',
          () => this.resetMessages()
        );

        this.addEventListener(
          'keydown',
          (event) => {
            if (event.key !== 'Enter') return;

            event.preventDefault();
            this.calculate();
          }
        );

        this.selectButton?.addEventListener(
          'click',
          () => this.selectSize()
        );
      }

      registerConciergeBridge() {
        const registry =
          window.__IIG_SIZE_FINDER_REGISTRY__ || [];

        if (!registry.includes(this)) {
          registry.push(this);
        }

        window.__IIG_SIZE_FINDER_REGISTRY__ = registry;
        window.IIGSizeFinderBridge = {
          current() {
            const connected = registry.filter(
              (item) => item?.isConnected
            );
            const pathname = window.location.pathname;

            return connected.find((item) => {
              try {
                return new URL(
                  item.dataset.productUrl || '',
                  window.location.origin
                ).pathname === pathname;
              } catch (error) {
                return false;
              }
            }) || connected[0] || null;
          }
        };

        window.dispatchEvent(
          new CustomEvent('iig-size-finder:ready')
        );
      }

      getConciergeContext() {
        return {
          eligible: this.isEligible(),
          metrics: this.metricOrder.filter(
            (metric) => this.metricRows.has(metric)
          ),
          productTitle:
            document.querySelector('h1')?.textContent?.trim() ||
            document.title,
          productUrl:
            this.dataset.productUrl || window.location.pathname
        };
      }

      recommendForConcierge(values = {}) {
        this.metricOrder.forEach((metric) => {
          const input = this.querySelector(
            `[data-size-finder-input="${metric}"]`
          );

          if (input) {
            input.value = values[metric] ?? '';
          }
        });

        this.lastConciergeResult = null;
        this.calculate();
        return this.lastConciergeResult;
      }

      async addConciergeRecommendation() {
        const recommendation = this.lastConciergeResult;
        const variantId = recommendation?.variantId;
        const recommendedSize = recommendation?.size;

        if (!/^[1-9]\d*$/.test(String(variantId || '')) || !Number.isSafeInteger(Number(variantId))) {
          throw new Error(
            this.dataset.variantMissing ||
            this.dataset.unavailableMessage
          );
        }

        const cartDrawer =
          document.querySelector('cart-drawer');
        const renderContext = cartDrawer?.getRenderContext?.();
        const sections = cartDrawer
          ? cartDrawer.getSectionsToRender()
              .map((section) => section.id)
          : [];
        const rootUrl =
          window.Shopify?.routes?.root || '/';
        const response = await fetch(
          `${rootUrl}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              id: Number(variantId),
              quantity: 1,
              properties: {
                _added_from_concierge: 'true'
              },
              sections,
              sections_url: window.location.pathname
            })
          }
        );
        const result = await response.json();

        const confirmedItems = Array.isArray(result?.items) ? result.items : [result];
        const confirmedVariant = confirmedItems.some((item) => item && String(item.variant_id ?? item.id) === String(variantId) && Number.isInteger(item.quantity) && item.quantity > 0);
        if (!response.ok || result?.status || result?.ok === false || !confirmedVariant) {
          throw new Error(
            result?.description ||
            this.dataset.unavailableMessage
          );
        }

        document.dispatchEvent(
          new CustomEvent('iig:cart-added', {
            detail: {
              source: 'assistant',
              context: 'size-finder',
              productVariantId: String(variantId)
            }
          })
        );

        if (cartDrawer && (renderContext !== undefined || (result.sections && sections.every((id) => typeof result.sections[id] === 'string' && result.sections[id].trim())))) {
          try { cartDrawer.renderContents(result, renderContext); } catch (error) { window.location.assign(`${rootUrl}cart`); }
        } else {
          window.location.assign(`${rootUrl}cart`);
        }

        return {
          ok: true,
          variantId: String(variantId),
          size: recommendedSize
        };
      }

      decodeTextDatasets() {
        [
          'recommendedTemplate',
          'betweenTemplate',
          'multipleTemplate',
          'nearestTemplate',
          'exactMessage',
          'limitedMessage',
          'betweenMessage',
          'multipleMessage',
          'nearestMessage',
          'unavailableMessage',
          'alternativeTemplate',
          'missingMeasurements',
          'invalidMeasurements',
          'noMatchingData',
          'noReliableMatch',
          'variantMissing'
        ].forEach((key) => {
          const value = this.dataset[key];

          if (!value?.includes('&')) return;

          const decoder =
            document.createElement('textarea');

          decoder.innerHTML = value;
          this.dataset[key] = decoder.value;
        });
      }

      parseJson(selector) {
        const script = this.querySelector(selector);

        if (!script) return null;

        try {
          let value = JSON.parse(
            script.textContent.trim()
          );

          if (typeof value === 'string') {
            value = JSON.parse(value);
          }

          return value;
        } catch (error) {
          return null;
        }
      }

      normalize(value) {
        return String(value ?? '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[łŁ]/g, 'l')
          .replace(/[øØ]/g, 'o')
          .replace(/[đĐðÐ]/g, 'd')
          .replace(/[þÞ]/g, 'th')
          .replace(/[æÆ]/g, 'ae')
          .replace(/[œŒ]/g, 'oe')
          .replace(/ß/g, 'ss')
          .toLowerCase()
          .replace(/[^\p{L}\p{N}]+/gu, ' ')
          .trim();
      }

      normalizeSize(value) {
        return this.normalize(value)
          .replace(/\s+/g, '');
      }

      getDatasets() {
        const result = [];

        if (
          Array.isArray(this.chart?.sizes) &&
          Array.isArray(this.chart?.rows)
        ) {
          result.push({
            sizes: this.chart.sizes,
            rows: this.chart.rows
          });
        }

        if (Array.isArray(this.chart?.sections)) {
          this.chart.sections.forEach(
            (section) => {
              if (
                Array.isArray(section?.sizes) &&
                Array.isArray(section?.rows)
              ) {
                result.push({
                  sizes: section.sizes,
                  rows: section.rows
                });
              }
            }
          );
        }

        return result;
      }

      getSizes() {
        const seen = new Set();
        const result = [];

        this.datasets.forEach((dataset) => {
          dataset.sizes.forEach((size) => {
            const key = this.normalizeSize(size);

            if (!key || seen.has(key)) return;

            seen.add(key);

            result.push({
              label: String(size),
              key
            });
          });
        });

        return result;
      }

      metricFromLabel(label) {
        const value = this.normalize(label);

        if (
          !value ||
          this.lengthTerms.some(
            (term) => value.includes(term)
          )
        ) {
          return null;
        }

        return (
          Object.keys(this.metricTerms).find(
            (metric) => {
              return this.metricTerms[metric].some(
                (term) => value.includes(term)
              );
            }
          ) || null
        );
      }

      parseCell(value) {
        if (
          typeof value === 'number' &&
          Number.isFinite(value) && value > 0
        ) {
          return {
            min: value,
            max: value
          };
        }

        if (
          value === null ||
          value === undefined
        ) {
          return null;
        }

        const matches = String(value)
          .replace(/,/g, '.')
          // A dash between measurements is a range separator, not a minus sign.
          .replace(/(\d)\s*[-–—]\s*(?=\d)/g, '$1–')
          .match(/-?\d+(?:\.\d+)?/g);

        if (!matches?.length) return null;

        const numbers = matches
          .map(Number.parseFloat)
          .filter(Number.isFinite);

        if (!numbers.length || numbers.some((number) => number <= 0)) return null;

        if (numbers.length === 1) {
          return {
            min: numbers[0],
            max: numbers[0]
          };
        }

        return {
          min: Math.min(
            numbers[0],
            numbers[1]
          ),

          max: Math.max(
            numbers[0],
            numbers[1]
          )
        };
      }

      getMetricRows() {
        const result = new Map();

        this.datasets.forEach((dataset) => {
          dataset.rows.forEach((row) => {
            const metric = this.metricFromLabel(
              `${row?.label ?? ''} ${
                row?.label_us ?? ''
              }`
            );

            if (
              !metric ||
              !Array.isArray(row?.values)
            ) {
              return;
            }

            const values = row.values.map(
              (value) => this.parseCell(value)
            );

            if (!values.some(Boolean)) return;

            if (!result.has(metric)) {
              result.set(metric, []);
            }

            result.get(metric).push({
              sizes: dataset.sizes,
              values
            });
          });
        });

        return result;
      }

      cellFor(metric, sizeKey) {
        const rows =
          this.metricRows.get(metric) || [];

        for (const row of rows) {
          const index = row.sizes.findIndex(
            (size) =>
              this.normalizeSize(size) === sizeKey
          );

          if (
            index !== -1 &&
            row.values[index]
          ) {
            return row.values[index];
          }
        }

        return null;
      }

      isEligible() {
  /*
   * Kalkulator pozostaje ukryty, gdy:
   * - nie ma poprawnej tabeli;
   * - tabela nie ma użytecznych pomiarów;
   * - produkt nie ma co najmniej dwóch rozmiarów;
   * - rozmiary tabeli nie odpowiadają wariantom produktu.
   */
  if (
    !this.chart ||
    !this.datasets.length ||
    !this.metricRows.size ||
    this.sizes.length < 2 ||
    this.sizeOptionIndex < 0
  ) {
    return false;
  }

  const oneSizeKeys = new Set(
    [
      'Universal',
      'One size',
      'One-size',
      'Uniwersalny',
      'Uniwersalna',
      'Unica',
      'Unico',
      'Taille unique',
      'Einheitsgröße',
      'Jedna velikost',
      'Egy méret'
    ].map((value) =>
      this.normalizeSize(value)
    )
  );

  /*
   * Pobieramy rzeczywiste rozmiary występujące
   * w wariantach aktualnego produktu.
   */
  const variantSizeKeys = new Set(
    this.variants
      .map((variant) =>
        this.normalizeSize(
          variant.options?.[
            this.sizeOptionIndex
          ]
        )
      )
      .filter(Boolean)
  );

  /*
   * Do rekomendacji wymagamy przynajmniej
   * jednego pomiaru obwodowego.
   *
   * Sam wymiar długości lub sam wymiar ramion
   * nie wystarcza do wiarygodnej rekomendacji.
   */
  const recommendationMetrics = [
    'bust',
    'underbust',
    'waist',
    'hips'
  ];

  const eligibleSizes = this.sizes.filter(
    (size) => {
      if (
        oneSizeKeys.has(size.key) ||
        !variantSizeKeys.has(size.key)
      ) {
        return false;
      }

      return recommendationMetrics.some(
        (metric) =>
          this.cellFor(metric, size.key)
      );
    }
  );

  /*
   * Przycisk pojawi się tylko wtedy, gdy
   * co najmniej dwa rzeczywiste warianty produktu
   * mają użyteczne dane w tabeli rozmiarów.
   */
  return eligibleSizes.length >= 2;
}

      showRelevantFields() {
        this.metricOrder.forEach((metric) => {
          const field = this.querySelector(
            `[data-size-finder-field="${metric}"]`
          );

          if (field) {
            field.hidden =
              !this.metricRows.has(metric);
          }
        });
      }

      findSizeOptionIndex() {
        const sizeKeys = new Set(
          this.sizes.map((size) => size.key)
        );

        const maxOptions = Math.max(
          ...this.variants.map(
            (variant) =>
              variant.options?.length || 0
          ),
          0
        );

        let bestIndex = -1;
        let bestMatches = 0;

        for (
          let index = 0;
          index < maxOptions;
          index += 1
        ) {
          const matches =
            this.variants.filter(
              (variant) =>
                sizeKeys.has(
                  this.normalizeSize(
                    variant.options?.[index]
                  )
                )
            ).length;

          if (matches > bestMatches) {
            bestMatches = matches;
            bestIndex = index;
          }
        }

        return bestMatches ? bestIndex : -1;
      }

      getMeasurements() {
        const measurements = [];
        let invalid = false;

        this.metricOrder.forEach((metric) => {
          if (!this.metricRows.has(metric)) {
            return;
          }

          const input = this.querySelector(
            `[data-size-finder-input="${metric}"]`
          );

          const raw = input?.value
            .trim()
            .replace(',', '.');

          if (!raw) return;

          const value =
            Number.parseFloat(raw);

          const minimum =
            metric === 'shoulder'
              ? 20
              : 40;

          const maximum =
            metric === 'shoulder'
              ? 80
              : 250;

          if (
            !Number.isFinite(value) ||
            value < minimum ||
            value > maximum
          ) {
            invalid = true;
            return;
          }

          measurements.push({
            metric,

            target:
              metric === 'shoulder'
                ? value
                : value / 2
          });
        });

        return {
          measurements,
          invalid
        };
      }

      variantsForSize(sizeKey) {
        if (this.sizeOptionIndex < 0) {
          return [];
        }

        const matches =
          this.variants.filter(
            (variant) =>
              this.normalizeSize(
                variant.options?.[
                  this.sizeOptionIndex
                ]
              ) === sizeKey
          );

        const selected =
          this.variants.find(
            (variant) =>
              String(variant.id) ===
              String(
                this.dataset.selectedVariantId
              )
          );

        if (
          !selected ||
          matches.length < 2
        ) {
          return matches;
        }

        return [...matches].sort(
          (first, second) => {
            const score = (variant) =>
              variant.options.reduce(
                (
                  sum,
                  option,
                  index
                ) => {
                  if (
                    index ===
                    this.sizeOptionIndex
                  ) {
                    return sum;
                  }

                  return (
                    sum +
                    (
                      this.normalize(option) ===
                      this.normalize(
                        selected.options?.[index]
                      )
                        ? 1
                        : 0
                    )
                  );
                },
                0
              );

            return (
              score(second) -
              score(first)
            );
          }
        );
      }

      evaluateMetric(size, measurement) {
        const cell = this.cellFor(
          measurement.metric,
          size.key
        );

        if (!cell) return null;

        const tightness = Math.max(
          0,
          measurement.target - cell.max
        );

        const looseness = Math.max(
          0,
          cell.min - measurement.target
        );

        return {
          safe:
            tightness <= this.tolerance,

          score:
            tightness * 1.5 +
            looseness
        };
      }

      evaluateSize(
        size,
        index,
        measurements
      ) {
        const results = measurements
          .map((measurement) =>
            this.evaluateMetric(
              size,
              measurement
            )
          )
          .filter(Boolean);

        if (!results.length) return null;

        const variants =
          this.variantsForSize(size.key);

        const availableVariant =
          variants.find(
            (variant) => variant.available
          ) || null;

        return {
          ...size,
          index,

          used: results.length,

          coverage:
            results.length /
            measurements.length,

          failures:
            results.filter(
              (result) => !result.safe
            ).length,

          averageScore:
            results.reduce(
              (sum, result) =>
                sum + result.score,
              0
            ) / results.length,

          variant:
            availableVariant ||
            variants[0] ||
            null,

          availableVariant
        };
      }

      bestSizeForMetric(measurement) {
        const candidates = this.sizes
          .map((size, index) => {
            const result =
              this.evaluateMetric(
                size,
                measurement
              );

            if (!result?.safe) {
              return null;
            }

            return {
              ...size,
              index,
              score: result.score
            };
          })
          .filter(Boolean)
          .sort(
            (first, second) =>
              first.score -
                second.score ||
              first.index -
                second.index
          );

        return candidates[0] || null;
      }

      replaceTokens(template, values) {
        return Object.entries(values).reduce(
          (text, [key, value]) => {
            return String(text ?? '')
              .replaceAll(
                `[[${key}]]`,
                value ?? ''
              );
          },
          String(template ?? '')
        );
      }

      calculate() {
        this.resetMessages();

        const {
          measurements,
          invalid
        } = this.getMeasurements();

        if (invalid) {
          this.showError(
            this.dataset.invalidMeasurements
          );

          return;
        }

        if (!measurements.length) {
          this.showError(
            this.dataset.missingMeasurements
          );

          return;
        }

        const evaluated = this.sizes
          .map((size, index) =>
            this.evaluateSize(
              size,
              index,
              measurements
            )
          )
          .filter(Boolean);

        if (!evaluated.length) {
          this.showNoResult(
            this.dataset.noMatchingData
          );

          return;
        }

        const maximumCoverage =
          Math.max(
            ...evaluated.map(
              (candidate) =>
                candidate.coverage
            )
          );

        const safeCandidates =
          evaluated
            .filter(
              (candidate) =>
                candidate.failures === 0 &&
                candidate.coverage ===
                  maximumCoverage
            )
            .sort(
              (first, second) =>
                first.averageScore -
                  second.averageScore ||
                first.index -
                  second.index
            );

        if (!safeCandidates.length) {
          const nearestCandidates =
            evaluated
              .filter(
                (candidate) =>
                  candidate.coverage ===
                    maximumCoverage
              )
              .sort(
                (first, second) =>
                  first.failures -
                    second.failures ||
                  first.averageScore -
                    second.averageScore ||
                  first.index -
                    second.index
              );

          if (!nearestCandidates.length) {
            this.showNoResult(
              this.dataset.noReliableMatch
            );

            return;
          }

          this.showRecommendation(
            nearestCandidates[0],
            {
              status: 'nearest',
              lowerSize: null,
              limited: true,
              safeCandidates:
                nearestCandidates
            }
          );

          return;
        }

        const suggestions =
          measurements
            .map((measurement) =>
              this.bestSizeForMetric(
                measurement
              )
            )
            .filter(Boolean);

        if (!suggestions.length) {
          this.showNoResult(
            this.dataset.noReliableMatch
          );

          return;
        }

        const indexes = [
          ...new Set(
            suggestions.map(
              (suggestion) =>
                suggestion.index
            )
          )
        ].sort(
          (first, second) =>
            first - second
        );

        const firstIndex = indexes[0];

        const lastIndex =
          indexes[indexes.length - 1];

        let status = 'recommended';

        if (
          indexes.length === 2 &&
          lastIndex - firstIndex === 1
        ) {
          status = 'between';
        } else if (indexes.length > 1) {
          status = 'multiple';
        }

        let recommendation =
          safeCandidates.find(
            (candidate) =>
              candidate.index === lastIndex
          ) || safeCandidates[0];

        if (
          status === 'between' &&
          recommendation.index !== lastIndex
        ) {
          status = 'multiple';
        }

        if (status === 'recommended') {
          recommendation =
            safeCandidates.find(
              (candidate) =>
                candidate.index ===
                firstIndex
            ) || safeCandidates[0];
        }

        this.showRecommendation(
          recommendation,
          {
            status,

            lowerSize:
              status === 'between'
                ? this.sizes[firstIndex]
                : null,

            limited:
              recommendation.used < 2 ||
              recommendation.coverage < 1,

            safeCandidates
          }
        );
      }

      showRecommendation(
        recommendation,
        context
      ) {
        let title = this.replaceTokens(
          this.dataset.recommendedTemplate,
          {
            size: recommendation.label
          }
        );

        let message =
          context.limited
            ? this.dataset.limitedMessage
            : this.dataset.exactMessage;

        if (context.status === 'nearest') {
          title = this.replaceTokens(
            this.dataset.nearestTemplate ||
              this.dataset.recommendedTemplate,
            {
              size: recommendation.label
            }
          );

          message =
            this.dataset.nearestMessage ||
            this.dataset.limitedMessage;
        } else if (
          context.status === 'between' &&
          context.lowerSize
        ) {
          title = this.replaceTokens(
            this.dataset.betweenTemplate,
            {
              first:
                context.lowerSize.label,

              second:
                recommendation.label
            }
          );

          message =
            this.dataset.betweenMessage;
        } else if (
          context.status === 'multiple'
        ) {
          title = this.replaceTokens(
            this.dataset.multipleTemplate,
            {
              size:
                recommendation.label
            }
          );

          message =
            this.dataset.multipleMessage;
        }

        let secondary = '';

        let selectableCandidate =
          recommendation.availableVariant
            ? recommendation
            : null;

        if (!recommendation.variant) {
          secondary =
            this.dataset.variantMissing;
        } else if (
          !recommendation.availableVariant
        ) {
          secondary =
            this.dataset.unavailableMessage;
        }

        if (!selectableCandidate) {
          const alternatives =
            context.safeCandidates
              .filter(
                (candidate) =>
                  candidate.index !==
                    recommendation.index &&
                  candidate.availableVariant
              )
              .sort(
                (first, second) => {
                  const firstPenalty =
                    first.index <
                    recommendation.index
                      ? 100
                      : 0;

                  const secondPenalty =
                    second.index <
                    recommendation.index
                      ? 100
                      : 0;

                  return (
                    firstPenalty +
                    Math.abs(
                      first.index -
                        recommendation.index
                    )
                  ) - (
                    secondPenalty +
                    Math.abs(
                      second.index -
                        recommendation.index
                    )
                  );
                }
              );

          const alternative =
            alternatives[0];

          if (alternative) {
            selectableCandidate =
              alternative;

            secondary =
              `${secondary} ${
                this.replaceTokens(
                  this.dataset
                    .alternativeTemplate,
                  {
                    size:
                      alternative.label
                  }
                )
              }`.trim();
          }
        }

        this.resultTitle.textContent =
          title;

        this.resultMessage.textContent =
          message;

        this.resultSecondary.textContent =
          secondary;

        this.resultSecondary.hidden =
          !secondary;

        if (
          selectableCandidate
            ?.availableVariant
        ) {
          this.selectButton.hidden = false;

          this.selectButton.dataset.variantId =
            selectableCandidate
              .availableVariant.id;

          this.selectButtonSize.textContent =
            selectableCandidate.label;
        } else {
          this.selectButton.hidden = true;

          this.selectButton.dataset.variantId =
            '';

          this.selectButtonSize.textContent =
            '';
        }

        this.lastConciergeResult = {
          ok: true,
          title,
          message,
          secondary,
          size: selectableCandidate?.label || recommendation.label,
          variantId: selectableCandidate?.availableVariant
            ? String(selectableCandidate.availableVariant.id)
            : '',
          available: Boolean(
            selectableCandidate?.availableVariant
          )
        };

        this.resultElement.hidden = false;
      }

      showNoResult(message) {
        this.resultTitle.textContent = '';

        this.resultMessage.textContent =
          message ||
          this.dataset.noReliableMatch;

        this.resultSecondary.hidden = true;
        this.resultSecondary.textContent = '';

        this.selectButton.hidden = true;
        this.selectButton.dataset.variantId = '';

        this.lastConciergeResult = {
          ok: false,
          error:
            message || this.dataset.noReliableMatch
        };

        this.resultElement.hidden = false;
      }

      showError(message) {
        this.errorElement.textContent =
          message || '';

        this.lastConciergeResult = {
          ok: false,
          error: message || ''
        };

        this.errorElement.hidden = false;
      }

      resetMessages() {
        this.errorElement.hidden = true;
        this.errorElement.textContent = '';

        this.resultElement.hidden = true;
      }

      selectSize() {
        const variantId =
          this.selectButton
            ?.dataset.variantId;

        if (!variantId) return;

        const variant = this.variants.find(
          (item) =>
            String(item.id) ===
            String(variantId)
        );

        const sizeValue =
          variant?.options?.[
            this.sizeOptionIndex
          ];

        const productScope =
          this.closest('.product') ||
          this.closest('quick-add-modal') ||
          document;

        if (sizeValue) {
          const sizeKey =
            this.normalizeSize(sizeValue);

          const radios = Array.from(
            productScope.querySelectorAll(
              '[data-size-option] input[type="radio"]'
            )
          ).filter(
            (input) =>
              this.normalizeSize(input.value) ===
              sizeKey
          );

          if (radios.length) {
            radios.forEach((radio) =>
              radio.click()
            );
            this.closest('modal-dialog')?.hide();
            return;
          }

          const selects = Array.from(
            productScope.querySelectorAll(
              '[data-size-option] select'
            )
          ).filter((element) =>
            Array.from(element.options).some(
              (option) =>
                this.normalizeSize(
                  option.value
                ) === sizeKey
            )
          );

          if (selects.length) {
            selects.forEach((select) => {
              const option = Array.from(
                select.options
              ).find(
                (item) =>
                  this.normalizeSize(
                    item.value
                  ) === sizeKey
              );

              select.value = option.value;
              select.dispatchEvent(
                new Event('change', {
                  bubbles: true
                })
              );
            });
            this.closest('modal-dialog')?.hide();
            return;
          }
        }

        const productUrl =
          this.dataset.productUrl ||
          window.location.pathname;

        const separator =
          productUrl.includes('?')
            ? '&'
            : '?';

        window.location.href =
          `${productUrl}${separator}` +
          `variant=${variantId}`;
      }
    }
  );
}

/* ===== PRODUCT SIZE FINDER END ===== */
